import { formatCountText } from "~/utils/strings";

// Constants for better maintainability
const SECONDS_IN_DAY = 60 * 60 * 24;
const JUST_NOW_THRESHOLD_SECONDS = 60;
const OVERDUE_THRESHOLD_DAYS = 30;

// New helper that returns either a string label ("Just now" | "Sent today") or
// a number representing full days elapsed.
export const getRemediationTimeValue = (time: string): string | number => {
  const now = new Date();
  const sentTime = new Date(time);
  const timeDifferenceInSeconds = (now.getTime() - sentTime.getTime()) / 1000;
  const timeDifferenceInDays = Math.floor(timeDifferenceInSeconds / SECONDS_IN_DAY);

  if (timeDifferenceInSeconds <= JUST_NOW_THRESHOLD_SECONDS) {
    return "Just now";
  } else if (timeDifferenceInDays === 0) {
    return "Sent today";
  }
  return timeDifferenceInDays;
};

/**
 * Formats remediation time display with optional sent count
 * @param time - ISO date string of when the remediation was sent
 * @param sentCount - Optional number of times the remediation was sent
 * @returns Object with structured data for table display
 */
export const getRemediationTime = (
  time: string,
  sentCount?: number,
): string | { label: string; value: string }[] => {
  const remediationTime = getRemediationTimeValue(time);
  const hasMultipleSends = sentCount && sentCount > 1;

  const createStructuredData = (lastSentText: string) => [
    { label: "No. of times sent", value: String(sentCount) },
    { label: "Last sent", value: lastSentText },
  ];

  if (typeof remediationTime === "string") {
    return hasMultipleSends ? createStructuredData(remediationTime) : remediationTime;
  }
  const dayText = formatCountText(remediationTime, "day");
  return createStructuredData(`${dayText} ago`);
};

/**
 * Determines if an invitation is overdue based on sent date
 * @param dateSent - ISO date string of when the invitation was sent, or null
 * @returns true if the invitation was sent more than 30 days ago
 */
export const isInvitationOverdue = (dateSent: string | null): boolean => {
  if (!dateSent) return false;

  const now = new Date();
  const sentTime = new Date(dateSent);
  const timeDifferenceInDays = (now.getTime() - sentTime.getTime()) / (1000 * SECONDS_IN_DAY);

  return timeDifferenceInDays > OVERDUE_THRESHOLD_DAYS;
};
